import React, { useCallback, useState } from "react";
import { Button, Flex, ModalV2, Textarea, toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { useMarkPaymentAsFailedMutation } from "../../../graphql/supergraphHooks";
import { clientContext } from "../../../utils/graphql_utils";
import { triggerPaymentBatchesRefresh } from "../../PaymentsListWorkflow/PaymentBatchesTable/refreshSignal";

interface MarkAsFailedModalProps {
  paymentId: string;
  payeeName: string;
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

const MarkAsFailedModal: React.FC<MarkAsFailedModalProps> = ({
  paymentId,
  payeeName,
  show,
  onHide,
  onSuccess,
}) => {
  const [failureReason, setFailureReason] = useState("");

  const [markPaymentAsFailed, { loading }] = useMarkPaymentAsFailedMutation({
    context: clientContext(),
    onError: (error) => {
      addError(formatError(error));
      toast.error(`Failed to mark payment as failed: ${error.message}`);
    },
    onCompleted: () => {
      toast.success("Payment marked as failed");
      setFailureReason("");
      onHide();
      onSuccess?.();
      triggerPaymentBatchesRefresh();
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!failureReason.trim()) {
      toast.error("Please provide a failure reason");
      return;
    }
    await markPaymentAsFailed({
      variables: {
        markPaymentAsFailedInput: {
          paymentId,
          failureReason: failureReason.trim(),
        },
      },
    });
  }, [failureReason, markPaymentAsFailed, paymentId]);

  const handleClose = useCallback(() => {
    setFailureReason("");
    onHide();
  }, [onHide]);

  return (
    <ModalV2 show={show} onHide={handleClose} title={"Mark Payment as Failed"}>
      <ModalV2.Body>
        <Flex flexDirection="column" gap={16}>
          <p>
            You are marking the payment to <strong>{payeeName}</strong> as failed. Please
            provide a reason for this failure.
          </p>
          <Textarea
            size="small"
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="Enter the reason for payment failure..."
            isFullWidth
          />
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" justifyContent="flex-end" gap={12}>
          <Button variant="neutral" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={loading || !failureReason.trim()}
            isLoading={loading}
          >
            Mark as Failed
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default MarkAsFailedModal;
